/**
 * ContactModal — a reusable, enterprise-grade lead-capture dialog.
 *
 * The lightweight sibling of PricingEngagement: a single-pane premium modal for
 * "Book a demo", "Ask a question" and "Request access" CTAs. It shares the same
 * visual language (branded header band, refined form, honest success state) and
 * routes through the resilient contact chain (Django → EmailJS → mailto), so a
 * lead is never lost. Use this everywhere a simple contact CTA is needed instead
 * of a raw `mailto:` link.
 */
import { useState } from "react";
import { X, ArrowRight, Loader2, Check, Lock } from "lucide-react";
import { submitContact, type ContactKind, type ContactResult } from "@/lib/contact";

interface Props {
  open: boolean;
  onClose: () => void;
  kind?: ContactKind;
  title?: string;
  subtitle?: string;
  cta?: string;
  defaultEmail?: string;
  defaultCompany?: string;
}

const inputCls =
  "w-full rounded-lg border border-border bg-bg/60 px-3 py-2 text-sm text-text " +
  "outline-none transition focus:border-accent/60 focus:ring-2 focus:ring-accent/15";

export function ContactModal({
  open, onClose, kind = "demo",
  title = "Book a demo",
  subtitle = "Tell us a little about your use case and we'll set up a 30-minute walkthrough on your data.",
  cta = "Request demo",
  defaultEmail = "", defaultCompany = "",
}: Props) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState(defaultEmail);
  const [company, setCompany] = useState(defaultCompany);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<ContactResult | null>(null);

  if (!open) return null;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSending(true);
    const res = await submitContact({
      kind, name, email, company,
      subject: `${title} — ${company || "new"}`,
      message,
      meta: { source: title },
    });
    setResult(res);
    setSending(false);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-[#05070b]/80 p-4 backdrop-blur-sm animate-fadeIn sm:items-center"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className="my-4 w-full max-w-md overflow-hidden rounded-2xl border border-border bg-panel shadow-2xl animate-slideIn"
        onClick={(e) => e.stopPropagation()}
      >
        {/* header band */}
        <div className="relative overflow-hidden bg-gradient-to-br from-[#0b1220] via-[#0d1526] to-[#070a12] px-6 py-5">
          <div
            className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full opacity-30 blur-2xl"
            style={{ background: "radial-gradient(closest-side, hsl(var(--accent)), transparent)" }}
            aria-hidden
          />
          <button
            type="button"
            onClick={onClose}
            className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-lg text-white/60 transition hover:bg-white/10 hover:text-white"
            aria-label="Close"
          >
            <X size={16} />
          </button>
          <div className="relative text-[15px] font-bold tracking-[0.14em] text-white">
            ONTOLIGENT<span className="text-good">.</span>
          </div>
          <h3 className="relative mt-3 text-xl font-semibold tracking-tight text-white">{title}</h3>
          <p className="relative mt-1.5 text-[13px] leading-relaxed text-white/65">{subtitle}</p>
        </div>

        <div className="p-6">
          {result?.ok ? (
            <div className="flex flex-col items-center py-6 text-center">
              <div className="grid h-12 w-12 place-items-center rounded-full border border-good/30 bg-good/10 text-good">
                <Check size={22} />
              </div>
              <h4 className="mt-3 text-lg font-semibold">Request received</h4>
              <p className="mt-2 max-w-xs text-sm leading-relaxed text-muted">
                {result.method === "mailto"
                  ? "Your request is ready in your email client — send it and we'll reply within one business day."
                  : "We've logged your request and a Redpill architect will reply within one business day."}
              </p>
              <button className="btn-primary mt-5" onClick={onClose}>Done</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="block"><span className="mb-1 block text-[12px] font-medium text-text/80">Name</span>
                  <input className={inputCls} required value={name} onChange={(e) => setName(e.target.value)} /></label>
                <label className="block"><span className="mb-1 block text-[12px] font-medium text-text/80">Work email</span>
                  <input className={inputCls} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} /></label>
              </div>
              <label className="block"><span className="mb-1 block text-[12px] font-medium text-text/80">Company</span>
                <input className={inputCls} value={company} onChange={(e) => setCompany(e.target.value)} /></label>
              <label className="block"><span className="mb-1 block text-[12px] font-medium text-text/80">What would you like to see?</span>
                <textarea className={`${inputCls} min-h-[80px] resize-y`} value={message}
                          placeholder="e.g. triage a redacted claim from our domain, with lineage"
                          onChange={(e) => setMessage(e.target.value)} /></label>
              <button className="btn-primary w-full justify-center py-2.5" type="submit" disabled={sending}>
                {sending ? <><Loader2 size={15} className="animate-spin" /> Sending…</> : <>{cta} <ArrowRight size={15} /></>}
              </button>
              <p className="flex items-center justify-center gap-1.5 text-[11px] text-muted">
                <Lock size={11} /> Confidential. We reply within one business day.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
